import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Switch,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useColors, ThemeColors } from '../../hooks/useColors';
import { spacing, typography } from '../../theme';
import { supabase } from '../../lib/supabase';
import { Vendor } from '../../types/vendor';

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday', short: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday', label: 'Thursday', short: 'Thu' },
  { key: 'friday', label: 'Friday', short: 'Fri' },
  { key: 'saturday', label: 'Saturday', short: 'Sat' },
  { key: 'sunday', label: 'Sunday', short: 'Sun' },
] as const;

type DayKey = typeof DAYS_OF_WEEK[number]['key'];

interface DayHours {
  isOpen: boolean;
  openTime: string;
  closeTime: string;
  is24Hours?: boolean;
}

interface StoreHours {
  [key: string]: DayHours;
  is24_7?: boolean;
}

const DEFAULT_HOURS: DayHours = {
  isOpen: true,
  openTime: '09:00',
  closeTime: '17:00',
  is24Hours: false,
};

// All hours from midnight to midnight
const TIME_OPTIONS = [
  '00:00', '00:30', '01:00', '01:30', '02:00', '02:30', '03:00', '03:30',
  '04:00', '04:30', '05:00', '05:30', '06:00', '06:30', '07:00', '07:30',
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
  '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00', '23:30',
];

interface StoreHoursSettingsProps {
  vendor: Vendor;
  onUpdate?: () => void;
}

const formatTime = (time24: string): string => {
  if (time24 === '00:00') return '12:00 AM';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

export function StoreHoursSettings({ vendor, onUpdate }: StoreHoursSettingsProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  
  const [is24_7, setIs24_7] = useState(false);
  const [hours, setHours] = useState<StoreHours>(() => {
    const defaultHours: StoreHours = {};
    DAYS_OF_WEEK.forEach(day => {
      defaultHours[day.key] = { ...DEFAULT_HOURS };
    });
    return defaultHours;
  });
  
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedDay, setExpandedDay] = useState<DayKey | null>(null);
  const [selectingTime, setSelectingTime] = useState<{ day: DayKey; type: 'open' | 'close' } | null>(null);

  // Load existing hours from vendor
  useEffect(() => {
    if (vendor?.delivery_hours) {
      const existingHours = vendor.delivery_hours as StoreHours;
      
      // Check for 24/7 mode
      if (existingHours.is24_7) {
        setIs24_7(true);
      }
      
      const mergedHours: StoreHours = {};
      DAYS_OF_WEEK.forEach(day => {
        if (existingHours[day.key]) {
          mergedHours[day.key] = {
            isOpen: existingHours[day.key].isOpen ?? true,
            openTime: existingHours[day.key].openTime || '09:00',
            closeTime: existingHours[day.key].closeTime || '17:00',
            is24Hours: existingHours[day.key].is24Hours ?? false,
          };
        } else {
          mergedHours[day.key] = { ...DEFAULT_HOURS };
        }
      });
      
      setHours(mergedHours);
    }
  }, [vendor?.delivery_hours]);

  const updateDayHours = (day: DayKey, updates: Partial<DayHours>) => {
    setHours(prev => ({
      ...prev,
      [day]: { ...prev[day], ...updates },
    }));
    setHasChanges(true);
  };

  const toggle24_7 = (value: boolean) => {
    setIs24_7(value);
    const newHours: StoreHours = {};
    if (value) {
      // Set all days to 24 hours
      DAYS_OF_WEEK.forEach(day => {
        newHours[day.key] = { isOpen: true, openTime: '00:00', closeTime: '23:59', is24Hours: true };
      });
    } else {
      // Reset to default hours
      DAYS_OF_WEEK.forEach(day => {
        newHours[day.key] = { ...DEFAULT_HOURS };
      });
    }
    setHours(newHours);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!vendor?.id) return;
    
    setSaving(true);
    try {
      const dataToSave = { ...hours, is24_7 };
      const { error } = await supabase
        .from('vendors')
        .update({ delivery_hours: dataToSave })
        .eq('id', vendor.id);

      if (error) throw error;

      setHasChanges(false);
      onUpdate?.();
    } catch (err) {
      console.error('Error saving store hours:', err);
    } finally {
      setSaving(false);
    }
  };

  const copyToAllDays = (sourceDay: DayKey) => {
    const sourceHours = hours[sourceDay];
    const newHours: StoreHours = {};
    
    DAYS_OF_WEEK.forEach(day => {
      newHours[day.key] = { ...sourceHours };
    });
    
    setHours(newHours);
    setHasChanges(true);
  };

  const renderTimeSelector = (day: DayKey, type: 'open' | 'close') => {
    const isSelecting = selectingTime?.day === day && selectingTime?.type === type;
    const currentTime = type === 'open' ? hours[day].openTime : hours[day].closeTime;
    const otherTime = type === 'open' ? hours[day].closeTime : hours[day].openTime;

    // Filter available times to prevent same open/close time
    const availableTimes = TIME_OPTIONS.filter(time => {
      if (type === 'open') {
        // Open time must be before close time
        return time < otherTime;
      } else {
        // Close time must be after open time
        return time > otherTime;
      }
    });

    if (isSelecting) {
      return (
        <View style={styles.timeSelectorContainer}>
          <ScrollView 
            style={styles.timeScrollView} 
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
          >
            {availableTimes.map(time => (
              <TouchableOpacity
                key={time}
                style={[
                  styles.timeOption,
                  time === currentTime && styles.timeOptionSelected,
                ]}
                onPress={() => {
                  updateDayHours(day, { [type === 'open' ? 'openTime' : 'closeTime']: time });
                  setSelectingTime(null);
                }}
              >
                <Text style={[
                  styles.timeOptionText,
                  time === currentTime && styles.timeOptionTextSelected,
                ]}>
                  {formatTime(time)}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      );
    }

    return (
      <TouchableOpacity
        style={styles.timeButton}
        onPress={() => setSelectingTime({ day, type })}
        disabled={!hours[day].isOpen}
      >
        <Text style={[
          styles.timeButtonText,
          !hours[day].isOpen && styles.timeButtonTextDisabled,
        ]}>
          {formatTime(currentTime)}
        </Text>
        <Feather 
          name="chevron-down" 
          size={14} 
          color={hours[day].isOpen ? colors.text : colors.textLight} 
        />
      </TouchableOpacity>
    );
  };

  const getStatusText = (dayHours: DayHours) => {
    if (!dayHours.isOpen) return 'Closed';
    return `${formatTime(dayHours.openTime)} - ${formatTime(dayHours.closeTime)}`;
  };

  return (
    <View style={styles.container}>
      {/* 24/7 Toggle */}
      <TouchableOpacity 
        style={styles.option24_7}
        onPress={() => toggle24_7(!is24_7)}
      >
        <View style={styles.option24_7Info}>
          <Feather name="clock" size={18} color={colors.primary} />
          <Text style={styles.option24_7Text}>Open 24/7</Text>
        </View>
        <Switch
          value={is24_7}
          onValueChange={toggle24_7}
          trackColor={{ false: colors.border, true: colors.primaryLight }}
          thumbColor={is24_7 ? colors.primary : colors.textLight}
        />
      </TouchableOpacity>

      {!is24_7 && (
        <>
          {DAYS_OF_WEEK.map((day, index) => {
            const dayHours = hours[day.key];
            const isExpanded = expandedDay === day.key;

            return (
              <View key={day.key} style={styles.dayContainer}>
                <TouchableOpacity 
                  style={styles.dayHeader}
                  onPress={() => setExpandedDay(isExpanded ? null : day.key)}
                >
                  <Text style={styles.dayLabel}>{day.short}</Text>
                  <Text style={[
                    styles.dayStatus,
                    !dayHours.isOpen && styles.dayStatusClosed,
                  ]}>
                    {getStatusText(dayHours)}
                  </Text>
                  <View style={styles.dayActions}>
                    <Switch
                      value={dayHours.isOpen}
                      onValueChange={(value) => {
                        updateDayHours(day.key, { isOpen: value });
                      }}
                      trackColor={{ false: colors.border, true: colors.primaryLight }}
                      thumbColor={dayHours.isOpen ? colors.primary : colors.textLight}
                      style={styles.smallSwitch}
                    />
                    <Feather 
                      name={isExpanded ? 'chevron-up' : 'chevron-down'} 
                      size={16} 
                      color={colors.textLight} 
                    />
                  </View>
                </TouchableOpacity>

                {isExpanded && dayHours.isOpen && (
                  <View style={styles.expandedContent}>
                    <View style={styles.timeRow}>
                      <View style={styles.timeColumn}>
                        <Text style={styles.timeLabel}>Opens</Text>
                        {renderTimeSelector(day.key, 'open')}
                      </View>
                      <Text style={styles.timeSeparator}>–</Text>
                      <View style={styles.timeColumn}>
                        <Text style={styles.timeLabel}>Closes</Text>
                        {renderTimeSelector(day.key, 'close')}
                      </View>
                    </View>

                    {index === 0 && (
                      <TouchableOpacity
                        style={styles.copyButton}
                        onPress={() => copyToAllDays(day.key)}
                      >
                        <Feather name="copy" size={12} color={colors.primary} />
                        <Text style={styles.copyButtonText}>Apply to all</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </View>
            );
          })}
        </>
      )}

      <TouchableOpacity 
        style={[styles.saveButton, !hasChanges && styles.saveButtonDisabled]} 
        onPress={handleSave}
        disabled={saving || !hasChanges}
      >
        {saving ? (
          <ActivityIndicator size="small" color={colors.surface} />
        ) : (
          <Text style={styles.saveButtonText}>Save Hours</Text>
        )}
      </TouchableOpacity>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    paddingHorizontal: spacing.lg,
  },
  option24_7: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.surface,
    padding: spacing.sm,
    borderRadius: 8,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  option24_7Info: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  option24_7Text: {
    ...typography.body,
    fontWeight: '600',
    color: colors.text,
  },
  dayContainer: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    marginBottom: 4,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.border,
  },
  dayHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: spacing.sm,
  },
  dayLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text,
    width: 36,
  },
  dayStatus: {
    flex: 1,
    fontSize: 12,
    color: colors.primary,
    fontWeight: '500',
  },
  dayStatusClosed: {
    color: colors.textLight,
  },
  dayActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  smallSwitch: {
    transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }],
  },
  expandedContent: {
    borderTopWidth: 1,
    borderTopColor: colors.border,
    padding: spacing.sm,
    backgroundColor: colors.backgroundSecondary,
  },
  timeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  timeColumn: {
    alignItems: 'center',
  },
  timeLabel: {
    fontSize: 10,
    color: colors.textLight,
    marginBottom: 2,
    fontWeight: '500',
  },
  timeSeparator: {
    fontSize: 12,
    color: colors.textLight,
    marginTop: 12,
  },
  timeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.border,
    gap: 4,
    minWidth: 90,
    justifyContent: 'center',
  },
  timeButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.text,
  },
  timeButtonTextDisabled: {
    color: colors.textLight,
  },
  timeSelectorContainer: {
    backgroundColor: colors.surface,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.border,
    maxHeight: 150,
    width: 100,
  },
  timeScrollView: {
    padding: 2,
  },
  timeOption: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  timeOptionSelected: {
    backgroundColor: colors.primaryLight,
  },
  timeOptionText: {
    fontSize: 12,
    color: colors.text,
    textAlign: 'center',
  },
  timeOptionTextSelected: {
    color: colors.primary,
    fontWeight: '600',
  },
  copyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.sm,
    paddingVertical: 4,
    gap: 4,
  },
  copyButtonText: {
    fontSize: 11,
    color: colors.primary,
    fontWeight: '600',
  },
  saveButton: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.success,
    paddingVertical: 14,
    borderRadius: 8,
    marginTop: spacing.lg,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 17,
    color: colors.surface,
    fontWeight: '600',
  },
});
